import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check, Plus, Receipt, X } from 'lucide-react';
import { PageHeader } from '@/components/shared/page';
import { DataTable, type Column } from '@/components/shared/data-table';
import { Can } from '@/components/shared/can';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox, Select, Textarea } from '@/components/ui/misc';
import { FormError } from '@/features/auth/auth-layout';
import { useCompanyOptions } from '@/features/crm/api';
import { useUsers } from '@/features/admin/api';
import { useDebounce } from '@/hooks/use-debounce';
import { usePagination } from '@/hooks/use-pagination';
import { ApiRequestError } from '@/lib/api';
import { formatDate, formatMoney } from '@/lib/format';
import {
  useApproveExpense,
  useBankAccounts,
  useExpenseCategories,
  useExpenses,
  useRejectExpense,
  useSaveExpense,
  useVendors,
} from './api';
import { APPROVAL_LABELS, APPROVAL_VARIANT, PAYMENT_METHOD_LABELS } from './labels';
import type { Expense, PaymentMethod } from './types';

const METHODS = Object.keys(PAYMENT_METHOD_LABELS) as PaymentMethod[];

export function ExpensesPage() {
  const pagination = usePagination();
  const [params] = useSearchParams();
  const [search, setSearch] = useState('');
  const [approvalStatus, setApprovalStatus] = useState('');
  const [unpaidOnly, setUnpaidOnly] = useState(params.get('unpaidOnly') === 'true');
  const [editing, setEditing] = useState<Expense | null>(null);
  const [creating, setCreating] = useState(false);
  const [rejecting, setRejecting] = useState<Expense | null>(null);

  const debounced = useDebounce(search);
  const approve = useApproveExpense();

  const { data, isLoading, error, refetch } = useExpenses({
    page: pagination.page,
    limit: pagination.limit,
    q: debounced || undefined,
    approvalStatus: approvalStatus || undefined,
    unpaidOnly: unpaidOnly ? 'true' : undefined,
  });

  const columns: Column<Expense>[] = [
    {
      key: 'description',
      header: 'Expense',
      cell: (row) => (
        <div className="min-w-0">
          <div className="truncate font-medium">{row.description}</div>
          <div className="truncate text-xs text-muted-foreground">
            {row.number} · {row.category?.name ?? 'Uncategorised'}
          </div>
        </div>
      ),
    },
    {
      key: 'vendor',
      header: 'Vendor',
      className: 'text-sm text-muted-foreground',
      cell: (row) => row.vendor?.name ?? '—',
    },
    {
      key: 'date',
      header: 'Date',
      className: 'whitespace-nowrap text-sm text-muted-foreground',
      cell: (row) => formatDate(row.date),
    },
    {
      key: 'amount',
      header: 'Amount',
      className: 'tabular text-sm font-medium',
      cell: (row) => formatMoney(row.total, row.currency),
    },
    {
      key: 'flags',
      header: '',
      cell: (row) => (
        <div className="flex flex-wrap gap-1">
          {row.isBillable && (
            <Badge variant="default" className="text-[10px]">
              Billable
            </Badge>
          )}
          {!row.isPaid && (
            <Badge variant="warning" className="text-[10px]">
              Unpaid
            </Badge>
          )}
          {row.reimbursementStatus === 'PENDING' && (
            <Badge variant="secondary" className="text-[10px]">
              To reimburse
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Approval',
      cell: (row) => (
        <Badge variant={APPROVAL_VARIANT[row.approvalStatus]}>
          {APPROVAL_LABELS[row.approvalStatus]}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      cell: (row) =>
        row.approvalStatus === 'PENDING' ? (
          <Can permission="finance.expense.approve">
            <span className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => approve.mutate(row.id)}
                className="flex items-center gap-1 text-xs text-emerald-600 hover:underline"
              >
                <Check className="h-3 w-3" />
                Approve
              </button>
              <button
                onClick={() => setRejecting(row)}
                className="flex items-center gap-1 text-xs text-destructive hover:underline"
              >
                <X className="h-3 w-3" />
                Reject
              </button>
            </span>
          </Can>
        ) : null,
    },
  ];

  return (
    <div>
      <PageHeader
        title="Expenses"
        description="A claim becomes a cost when it is approved — that is when it reaches the books."
        actions={
          <Can permission="finance.expense.create">
            <Button onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" />
              New expense
            </Button>
          </Can>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Input
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            pagination.reset();
          }}
          placeholder="Search description, number or vendor…"
          className="h-9 w-full sm:max-w-xs"
        />
        <Select
          value={approvalStatus}
          onChange={(event) => {
            setApprovalStatus(event.target.value);
            pagination.reset();
          }}
          className="h-9 w-auto min-w-[10rem]"
        >
          <option value="">All</option>
          <option value="PENDING">Pending approval</option>
          <option value="APPROVED">Approved</option>
          <option value="REJECTED">Rejected</option>
        </Select>
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={unpaidOnly}
            onChange={(event) => {
              setUnpaidOnly(event.target.checked);
              pagination.reset();
            }}
          />
          Unpaid only
        </label>
      </div>

      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        error={error ? 'Could not load expenses.' : null}
        onRetry={() => void refetch()}
        meta={data?.meta}
        onPageChange={pagination.setPage}
        onRowClick={(row) => row.approvalStatus !== 'APPROVED' && setEditing(row)}
        emptyTitle="No expenses yet"
        emptyDescription="Record what the business spends so margin and cash mean something."
        emptyAction={
          <Can permission="finance.expense.create">
            <Button onClick={() => setCreating(true)}>
              <Receipt className="h-4 w-4" />
              New expense
            </Button>
          </Can>
        }
      />

      {(creating || editing) && (
        <ExpenseDialog
          expense={editing ?? undefined}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
      {rejecting && <RejectDialog expense={rejecting} onClose={() => setRejecting(null)} />}
    </div>
  );
}

function ExpenseDialog({ expense, onClose }: { expense?: Expense; onClose: () => void }) {
  const save = useSaveExpense();
  const { data: categories = [] } = useExpenseCategories();
  const { data: vendors = [] } = useVendors();
  const { data: bankAccounts = [] } = useBankAccounts();
  const { data: companies = [] } = useCompanyOptions();
  const { data: users } = useUsers({ limit: 100, status: 'ACTIVE' });
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    date: expense?.date.slice(0, 10) ?? new Date().toISOString().slice(0, 10),
    description: expense?.description ?? '',
    amount: expense?.amount ?? '',
    taxAmount: expense?.taxAmount ?? '',
    categoryId: expense?.categoryId ?? '',
    vendorId: expense?.vendorId ?? '',
    method: expense?.method ?? ('BANK_TRANSFER' as PaymentMethod),
    bankAccountId: expense?.bankAccountId ?? '',
    companyId: expense?.companyId ?? '',
    isBillable: expense?.isBillable ?? false,
    markupPercent: expense?.markupPercent ?? 0,
    paidByUserId: expense?.paidByUser?.id ?? '',
    isPaid: expense?.isPaid ?? true,
    dueDate: expense?.dueDate?.slice(0, 10) ?? '',
  });

  const set = (patch: Partial<typeof form>) => setForm((current) => ({ ...current, ...patch }));

  const submit = async () => {
    setError(null);
    try {
      await save.mutateAsync({
        ...(expense ? { id: expense.id } : {}),
        ...form,
        taxAmount: form.taxAmount || undefined,
      });
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not save the expense.');
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{expense ? `Edit ${expense.number}` : 'New expense'}</DialogTitle>
          <DialogDescription>
            It stays a claim until someone approves it — approval is what posts it to the ledger.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Description" className="sm:col-span-2">
            <Input
              value={form.description}
              autoFocus
              onChange={(event) => set({ description: event.target.value })}
            />
          </Field>
          <Field label="Amount (excluding tax)">
            <Input
              inputMode="decimal"
              placeholder="0.00"
              value={form.amount}
              onChange={(event) => set({ amount: event.target.value })}
            />
          </Field>
          <Field label="Tax">
            <Input
              inputMode="decimal"
              placeholder="0.00"
              value={form.taxAmount}
              onChange={(event) => set({ taxAmount: event.target.value })}
            />
          </Field>
          <Field label="Date">
            <Input
              type="date"
              value={form.date}
              onChange={(event) => set({ date: event.target.value })}
            />
          </Field>
          <Field label="Category">
            <Select
              value={form.categoryId}
              onChange={(event) => set({ categoryId: event.target.value })}
            >
              <option value="">Uncategorised</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Vendor">
            <Select
              value={form.vendorId}
              onChange={(event) => set({ vendorId: event.target.value })}
            >
              <option value="">Not set</option>
              {vendors.map((vendor) => (
                <option key={vendor.id} value={vendor.id}>
                  {vendor.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Method">
            <Select
              value={form.method}
              onChange={(event) => set({ method: event.target.value as PaymentMethod })}
            >
              {METHODS.map((value) => (
                <option key={value} value={value}>
                  {PAYMENT_METHOD_LABELS[value]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Paid from">
            <Select
              value={form.bankAccountId}
              onChange={(event) => set({ bankAccountId: event.target.value })}
            >
              <option value="">Not set</option>
              {bankAccounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Paid by an employee?" hint="Leave blank if the company paid directly.">
            <Select
              value={form.paidByUserId}
              onChange={(event) => set({ paidByUserId: event.target.value })}
            >
              <option value="">Company paid</option>
              {(users?.data ?? []).map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </Select>
          </Field>

          <div className="space-y-2 sm:col-span-2">
            <label className="flex cursor-pointer items-center gap-2.5">
              <Checkbox
                checked={form.isPaid}
                onCheckedChange={(checked) => set({ isPaid: checked === true })}
              />
              <span className="text-sm">
                Already paid
                <span className="block text-xs text-muted-foreground">
                  Unpaid expenses appear as payables on the dashboard.
                </span>
              </span>
            </label>

            {!form.isPaid && (
              <Field label="Due date">
                <Input
                  type="date"
                  value={form.dueDate}
                  onChange={(event) => set({ dueDate: event.target.value })}
                />
              </Field>
            )}

            <label className="flex cursor-pointer items-center gap-2.5">
              <Checkbox
                checked={form.isBillable}
                onCheckedChange={(checked) => set({ isBillable: checked === true })}
              />
              <span className="text-sm">Recharge to a client</span>
            </label>
          </div>

          {form.isBillable && (
            <>
              <Field label="Client">
                <Select
                  value={form.companyId}
                  onChange={(event) => set({ companyId: event.target.value })}
                >
                  <option value="">Choose a client</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Markup %">
                <Input
                  type="number"
                  min={0}
                  value={form.markupPercent}
                  onChange={(event) => set({ markupPercent: Number(event.target.value) })}
                />
              </Field>
            </>
          )}
        </div>

        <FormError message={error} />

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={save.isPending || form.description.trim().length < 2 || !form.amount}
          >
            {save.isPending ? 'Saving…' : expense ? 'Save changes' : 'Record expense'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RejectDialog({ expense, onClose }: { expense: Expense; onClose: () => void }) {
  const reject = useRejectExpense();
  const [reason, setReason] = useState('');

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reject {expense.number}?</DialogTitle>
          <DialogDescription>
            {expense.approvalStatus === 'APPROVED'
              ? 'This expense is already in the books, so rejecting it will reverse its ledger entry.'
              : 'The claim stays on record with your reason.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          <Label>Reason</Label>
          <Textarea rows={3} value={reason} onChange={(event) => setReason(event.target.value)} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={reason.trim().length < 3 || reject.isPending}
            onClick={async () => {
              await reject.mutateAsync({ id: expense.id, reason });
              onClose();
            }}
          >
            {reject.isPending ? 'Rejecting…' : 'Reject'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  hint,
  className,
  children,
}: {
  label: string;
  hint?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`space-y-1.5 ${className ?? ''}`}>
      <Label>{label}</Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}
